import { Trans, useTranslation } from 'react-i18next'
import { H1, H5, QuickActionButton } from '@/components/atoms'
import {
  AppDownloadBanner,
  BillingCard,
  IdCard,
  PromoCard,
  ReferModal,
} from '@/components/molecules'
import { petDefaultIdCardImage } from '@/shared/assets'
import { ROUTES } from '@/shared/constants/routes'
import { Link } from '@tanstack/react-router'
import { ChevronRightIcon } from '@/components/atoms/icons'
import { useHomePage } from './page.home.hook'

export function HomeDesktop() {
  const { t } = useTranslation()
  const {
    firstName,
    ownerName,
    isUserLoading,
    quickActions,
    showCoverages,
    coveragesLink,
    showBilling,
    billingItems,
    downloadBill,
    showRefer,
    isReferModalOpen,
    openReferModal,
    closeReferModal,
    showCrossSellRenters,
  } = useHomePage()

  return (
    <div className="flex flex-col gap-6" data-testid="home-page">
      <div className="flex max-w-236.5 items-end justify-between gap-6">
        <div className="flex min-w-0 flex-col gap-6.75 self-start pt-1.5">
          <H1
            className="text-[3.75rem]! leading-[normal]! font-semibold text-neutral-900"
            data-testid="home-greeting"
          >
            {firstName ? t('home.greeting', { name: firstName }) : ''}
          </H1>

          {quickActions.length > 0 && (
            <nav
              aria-label={t('home.quickActions.ariaLabel')}
              className="grid grid-cols-4 gap-2"
            >
              {quickActions.map((action) => (
                <QuickActionButton
                  key={action.to}
                  icon={action.icon}
                  label={action.label}
                  to={action.to}
                  testId={action.testId}
                  className="aspect-auto! h-auto! min-h-21 max-w-26.75"
                />
              ))}
            </nav>
          )}
        </div>

        {showCoverages && (
          <IdCard
            variant="with-photo"
            className="w-full max-w-83.5 shrink-0"
            photoUrl={petDefaultIdCardImage}
            hidePhotoBackground
            planName={t('home.coverages.title')}
            ownerName={ownerName}
            isLoading={isUserLoading}
            link={coveragesLink}
          />
        )}
      </div>

      <div
        role="region"
        aria-label={t('home.banners.ariaLabel')}
        tabIndex={0}
        className="scrollbar-hover -mr-16 flex snap-x snap-mandatory gap-4 overflow-x-auto pr-16 pb-2 focus:outline-none"
        data-testid="home-banners"
      >
        <AppDownloadBanner
          variant="compact"
          className="w-103! shrink-0 snap-start"
        />
        {showCrossSellRenters && (
          <PromoCard
            variant="indigo"
            layout="compact"
            className="w-106 shrink-0 snap-start"
            title={t('home.crossSellRenters.title')}
            titleHighlight={t('home.crossSellRenters.titleHighlight')}
            description={<Trans i18nKey="home.crossSellRenters.description" />}
            action={{
              label: t('home.crossSellRenters.cta'),
              onClick: () => {},
            }}
            testId="home-cross-sell-renters-card"
          />
        )}
        {showRefer && (
          <PromoCard
            variant="coral"
            layout="compact"
            className="w-106 shrink-0 snap-start"
            title={t('home.refer.title')}
            titleHighlight={t('home.refer.titleHighlight')}
            description={<Trans i18nKey="home.refer.description" />}
            action={{ label: t('home.refer.cta'), onClick: openReferModal }}
            testId="home-refer-card"
          />
        )}
      </div>

      {showBilling && (
        <section
          className="flex max-w-236.5 flex-col gap-6"
          data-testid="home-billing"
        >
          <div className="flex items-center justify-between">
            <H5 className="leading-6! text-neutral-900">
              {t('home.billing.title')}
            </H5>
            <Link
              to={ROUTES.BILLING}
              className="tracking-base text-indigo-base flex h-8 items-center gap-1 rounded-full px-4 text-xs leading-4 hover:underline"
              data-testid="home-billing-see-all"
            >
              {t('home.billing.seeAll')}
              <ChevronRightIcon className="size-3" />
            </Link>
          </div>
          <div className="grid grid-cols-4 gap-4">
            {billingItems.map((item) => (
              <BillingCard
                key={item.id}
                name={item.name}
                period={item.period}
                status={item.status}
                amount={item.amount}
                downloadLabel={t('home.billing.download')}
                onDownload={downloadBill}
                testId={item.id}
              />
            ))}
          </div>
        </section>
      )}

      {showRefer && (
        <ReferModal isOpen={isReferModalOpen} onClose={closeReferModal} />
      )}
    </div>
  )
}
